<script>
import api from "@/api";
import CardMixin from "@/mixins/CardMixin";
import MultiSelect from "@/components/MultiSelect.vue";
import { klona } from "klona";

const defaultUserData = {
  id: "",
  email: "",
  fullname: "",
  permissions: [],
  corpus_permissions: []
};

export default {
  mixins: [CardMixin],
  emits: ["reload"],
  components: { MultiSelect},
  props: {
    edit: {
      type: Object
    },
    permissions: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      formData: klona(defaultUserData),
      permissionBlocks: [
        {
          name: "Общие",
          check: x => !(x.startsWith("generators") || x.startsWith("corpus"))
        },
        {
          name: "Генераторы",
          check: x => x.startsWith("generators")
        },
        {
          name: "Корпусы",
          check: x => x.startsWith("corpus")
        }
      ]
    };
  },
  methods: {
    onShow() {
      if (this.edit.user) {
        this.formData.id = this.edit.user.id;
        this.formData.email = this.edit.user.email;
        this.formData.fullname = this.edit.user.fullname;
        this.edit.user.permissions.forEach(x => {
          this.formData.permissions.push(x.name);
        });
        this.$store.getters.CORPUS_LIST.forEach(x => {
          if (this.edit.user.corpus_permissions.includes(x.id)) {
            this.formData.corpus_permissions.push(x);
          }
        })
      }
    },
    async apiCall() {

      let url = `/manage/users/`;
      if (this.edit.user) {
        url += `${this.edit.user.id}/edit`;
      } else {
        url += "add";
      }

      return api.post(url, this.formData)
        .then(({ data }) => {
          if (!this.edit.user) {
            this.$bvToast.toast(
              `Пользователь "${data.email}" успешно создан. Для возможности входа установите пароль)`,
              { ...this.commonToaster, variant: "success" }
            );
          } else {
            this.$bvToast.toast(
              "Данные пользователя успешно изменены",
              { ...this.commonToaster, variant: "success" }
            );
          }
          this.$emit("reload");
          this.$bvModal.hide("modal-edit-user");
        })
        .catch(error => {
          if (error.response.status === 409) {
            switch (error.response.data) {
              case "email_exists":
                this.$bvToast.toast("Уже есть пользователь с таким email", {
                  ...this.commonToaster, variant: "danger"
                });
                break;
              case "id_exists":
                this.$bvToast.toast("Уже есть пользователь с таким id", {
                  ...this.commonToaster, variant: "danger"
                });
                break;
            }
          } else {
            let msg;
            if (this.edit.user) {
              msg = `Неизвестная ошибка при редактировании пользователя`;
            } else {
              msg = `Неизвестная ошибка при добавлении пользователя`;
            }
            this.$bvToast.toast(msg, { ...this.commonToaster, variant: "danger" });
            console.log(error.response);
          }
        });
    },
    clear() {
      this.formData = klona(defaultUserData);
    }
  },
  computed: {
    modalTitle() {
      if (this.edit.user) {
        return `Редактирование пользователя ${this.edit.user.fullname}`;
      }
      return "Добавление пользователя";
    },
    stateEmail() {
      return /(.+)@(.+){2,}\.(.+){2,}/.test(this.formData.email);
    },
    stateFullname() {
      return this.formData.fullname.length >= 3;
    }
  }
};
</script>

<template>
  <b-modal
    id="modal-edit-user"
    :busy="!(stateEmail && stateFullname) || loading"
    :title="modalTitle" centered lazy no-close-on-backdrop ok-only ok-title="Сохранить"
    scrollable
    size="lg"
    static
    @hide="clear"
    @show="onShow"
    @ok.prevent="save"
  >
    <b-form-group>
      <b-input-group>
        <b-input-group-prepend is-text>
          <font-awesome-icon fixed-width icon="id-badge" />
        </b-input-group-prepend>
        <b-form-input
          v-model.trim="formData.id"
          :disabled="!!edit.user"
          placeholder="Опционально идентификатор"
        />
      </b-input-group>
    </b-form-group>

    <b-form-group :state="stateEmail" invalid-feedback="Введите email">
      <b-input-group>
        <b-input-group-prepend is-text>
          <font-awesome-icon fixed-width icon="envelope" />
        </b-input-group-prepend>
        <b-form-input
          v-model.trim="formData.email"
          :formatter="(value) => value.toLowerCase()"
          :state="stateEmail"
          placeholder="Email"
          rows="3"
        />
      </b-input-group>
    </b-form-group>

    <b-form-group :state="stateFullname" invalid-feedback="Минимум 3 символа">
      <b-input-group>
        <b-input-group-prepend is-text>
          <font-awesome-icon fixed-width icon="user" />
        </b-input-group-prepend>
        <b-form-input
          v-model="formData.fullname"
          :state="stateFullname"
          placeholder="ФИО"
          trim
        />
      </b-input-group>
    </b-form-group>

    <h5>Разрешения:</h5>
    <b-tabs>
      <b-tab title="Список разрешений">
        <b-form-checkbox-group v-model="formData.permissions" stacked>
          <!--<b-card-group>-->
          <b-card
            v-for="(card, index) in permissionBlocks"
            :key="`perm-${index}`"
            :sub-title="card.name"
            class="mt-2"
          >
            <b-form-checkbox
              v-for="option in permissions"
              v-if="card.check(option.name)"
              :key="`perm-${option.name}`"
              :value="option.name"
            >
              {{ option.value }}
            </b-form-checkbox>
          </b-card>
          <!--</b-card-group>-->

        </b-form-checkbox-group>
      </b-tab>
      <b-tab
        v-if="!formData.permissions.includes('corpus_all')"
        :title="`Доступ к корпусам (${formData.corpus_permissions.length})`"
      >

        <MultiSelect
          v-model="formData.corpus_permissions"
          :items="[...$store.getters.CORPUS_LIST].sort((a, b) => a.name.localeCompare(b.name))"
          text-field="name"
          value-field="id"
          label="Доступ к корпусам"
          search-placeholder="Найти корпус..."
          select-all-label="Выделить все"
          deselect-all-label="Очистить"
          selected-label="Выбраны:"
        />

      </b-tab>
    </b-tabs>


  </b-modal>
</template>

<style scoped>
/* Управление шириной меню */
.adaptive-dropdown-menu {
  width: min(90vw, 600px); /* не шире 600px, но максимум 90% окна */
  min-width: 320px;
  padding: 0.5rem 1rem;
}

/* Прокрутка при длинном списке */
.adaptive-dropdown-form {
  max-height: calc(70vh - 100px); /* динамическая высота — 70% высоты окна */
  overflow-y: auto;
  overflow-x: hidden;
}

/* На случай узких экранов — кнопка тянется по ширине контейнера */
.adaptive-dropdown .btn {
  width: 100%;
  text-align: left;
}
</style>